import React, { useState, useRef, useEffect } from 'react'
import NotificationFilters from './Components/NotificationFilters'
import NotificationTable from './Components/NotificationTable'
import NotificationModal from './Components/NotificationModal'
import {
  useWindowResize,
  useNotificationFilters,
  handleExportToCSV,
} from './Components/utils'
import { layoutStyles } from './styles'
import {
  Box,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material'
import {
  buildNotificationParams,
  bulkUpdateNotifications,
  exportNotifications,
  getNotificationById,
  getNotifications,
  getNotificationStats,
  updateNotificationStatus,
} from '../../services/notificationService'

const NotificationView = () => {
  // State for notifications and UI
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 50,
    hasNextPage: false,
    hasPrevPage: false,
  })
  const [error, setError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [stats, setStats] = useState(null)

  const headerRef = useRef(null)

  // Custom hooks
  const { screenHeight, headerHeight } = useWindowResize(headerRef)
  const {
    searchName,
    setSearchName,
    selectedStatus,
    setSelectedStatus,
    selectedSeverity,
    setSelectedSeverity,
    selectedType,
    setSelectedType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
  } = useNotificationFilters([]) // Pass empty array since we're managing notifications via API

  // Fixed fetchNotifications function
  const fetchNotifications = async (page = 1, showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true)
      }
      setError('')

      const filters = {
        searchName,
        selectedStatus,
        selectedSeverity,
        selectedType,
        fromDate,
        toDate,
        page,
        limit: pagination.limit,
        sortBy: 'created_at',
        sortOrder: 'desc',
      }

      const params = buildNotificationParams(filters)
      const response = await getNotifications(params)

      console.log('Fetched notifications:', response.data.notifications)

      if (response) {
        // Fix: Use response.notifications directly (not response.data.notifications)
        setNotifications(response.data.notifications || [])

        // Set pagination from response
        setPagination(
          response.data.pagination || {
            currentPage: 1,
            totalPages: 0,
            totalCount: 0,
            limit: 50,
            hasNextPage: false,
            hasPrevPage: false,
          }
        )

        // Update stats if available (assuming stats might be in response.summary)
        if (response.summary) {
          setStats(response.summary)
        }
      } else {
        setError('Failed to fetch notifications')
        setNotifications([])
      }
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setError(error.message || 'Failed to fetch notifications')
      setNotifications([]) // Set empty array on error
    } finally {
      setLoading(false)
      setInitialLoading(false)
    }
  }
  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await getNotificationStats(30)
      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  // Effect to fetch notifications when filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchNotifications(1)
    }, 300) // Debounce API calls

    return () => clearTimeout(timeoutId)
  }, [
    searchName,
    selectedStatus,
    selectedSeverity,
    selectedType,
    fromDate,
    toDate,
  ])

  // Initial load
  useEffect(() => {
    fetchNotifications(1)
    fetchStats()
  }, [])

  // Event handlers
  const handleSearch = (e) => {
    setSearchName(e.target.value)
  }

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value)
  }

  const handleSeverityChange = (event) => {
    setSelectedSeverity(event.target.value)
  }

  const handleTypeChange = (event) => {
    setSelectedType(event.target.value)
  }

  const handleViewNotification = async (notification) => {
    try {
      // Fetch full notification details
      const response = await getNotificationById(notification.id)
      if (response.success) {
        setSelectedNotification(response.data)
        setOpenModal(true)
      }
    } catch (error) {
      setError('Failed to fetch notification details')
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedNotification(null)
  }

  const handleExport = async () => {
    try {
      setLoading(true)
      const filters = {
        searchName,
        selectedStatus,
        selectedSeverity,
        selectedType,
        fromDate,
        toDate,
      }

      const params = buildNotificationParams(filters)
      const exportData = await exportNotifications(params)
      handleExportToCSV(exportData)
      setSuccessMessage('Notifications exported successfully!')
    } catch (error) {
      setError('Failed to export notifications')
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (newPage) => {
    fetchNotifications(newPage)
  }

  const handleNotificationUpdate = async (id, updates) => {
    try {
      const response = await updateNotificationStatus(id, updates)
      if (response.success) {
        setSuccessMessage('Notification updated successfully!')
        // Refresh the current page
        fetchNotifications(pagination.currentPage, false)

        // Update the selected notification if it's open in modal
        if (selectedNotification && selectedNotification.id === id) {
          setSelectedNotification(response.data)
        }
      }
    } catch (error) {
      setError('Failed to update notification')
    }
  }

  const handleBulkUpdate = async (ids, updates) => {
    try {
      setLoading(true)
      const response = await bulkUpdateNotifications(ids, updates)
      if (response.success) {
        setSuccessMessage(
          `Successfully updated ${response.data.modifiedCount} notifications`
        )
        fetchNotifications(pagination.currentPage, false)
      }
    } catch (error) {
      setError('Failed to bulk update notifications')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseError = () => {
    setError('')
  }

  const handleCloseSuccess = () => {
    setSuccessMessage('')
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    )
  }

  return (
    <div style={layoutStyles.container}>
      {/* Error and Success Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Stats Summary */}
      {/* {stats && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="h6" color="primary">
                {stats.totalNotifications || 0}
              </Typography>
              <Typography variant="body2">Total Notifications</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h6" color="error">
                {stats.activeNotifications || 0}
              </Typography>
              <Typography variant="body2">Active</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h6" color="success.main">
                {stats.resolvedNotifications || 0}
              </Typography>
              <Typography variant="body2">Resolved</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="h6" color="warning.main">
                {stats.criticalNotifications || 0}
              </Typography>
              <Typography variant="body2">Critical</Typography>
            </Grid>
          </Grid>
        </Box>
      )} */}

      {/* Filters Section */}
      <NotificationFilters
        searchName={searchName}
        onSearchChange={handleSearch}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedSeverity={selectedSeverity}
        onSeverityChange={handleSeverityChange}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        fromDate={fromDate}
        onFromDateChange={setFromDate}
        toDate={toDate}
        onToDateChange={setToDate}
        onExport={handleExport}
        headerRef={headerRef}
        loading={loading}
      />

      {/* Table Section */}
      <NotificationTable
        notifications={notifications}
        loading={loading}
        screenHeight={screenHeight}
        headerHeight={headerHeight}
        onViewNotification={handleViewNotification}
        pagination={pagination}
        onPageChange={handlePageChange}
        onNotificationUpdate={handleNotificationUpdate}
        onBulkUpdate={handleBulkUpdate}
      />

      {/* Modal Section */}
      <NotificationModal
        open={openModal}
        onClose={handleCloseModal}
        notification={selectedNotification}
        onUpdate={handleNotificationUpdate}
      />
    </div>
  )
}

export default NotificationView
